import React from 'react';
import { X, FileText, Building2, AlertTriangle } from 'lucide-react';

const ConciliacaoDetalhesModal = ({ isOpen, onClose, item }) => {
  if (!isOpen || !item) return null;

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data) => {
    if (!data || data === '00/00/0000') return '-';
    // Se já está no formato brasileiro
    if (data.match(/^\d{2}\/\d{2}\/\d{4}/)) {
      return data.split(' ')[0]; // Remove hora se houver
    }
    // Se está no formato ISO
    const matchIso = data.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (matchIso) {
      return `${matchIso[3]}/${matchIso[2]}/${matchIso[1]}`;
    }
    return data;
  };

  // Verificar se é duplicidade
  const isDuplicidade = item.categoria === 'duplicidades';
  const ixcContas = item.ixcContas || (item.ixc ? [item.ixc] : []);
  
  // Dados do IXC (conta única)
  const ixc = !isDuplicidade ? (item.ixc || item.conta || null) : null;
  const isContaPaga = ixc?.debito_data || ixc?.valor_total_pago !== undefined;
  
  // Dados do OFX (transação)
  const ofx = item.ofx || item.transacao || null;

  // Componente para renderizar uma conta IXC
  const renderContaIxc = (conta, _idx = null) => {
    const _isPaga = conta?.debito_data || conta?.valor_total_pago !== undefined;
    
    return (
      <div className="space-y-3 text-sm">
        <div>
          <span className="text-[color:var(--text-muted)]">ID:</span>
          <span className="ml-2 font-medium text-[color:var(--text-primary)]">{conta.id || '-'}</span>
        </div>
        <div>
          <span className="text-[color:var(--text-muted)]">Nome:</span>
          <span className="ml-2 font-medium text-[color:var(--text-primary)]">{conta.nome || conta.fornecedor || conta.cliente || '-'}</span>
        </div>
        <div>
          <span className="text-[color:var(--text-muted)]">Valor:</span>
          <span className="ml-2 font-medium text-emerald-500">
            {formatarMoeda(conta.valor_total_pago || conta.valor_total_recebido || conta.valor)}
          </span>
        </div>
        <div>
          <span className="text-[color:var(--text-muted)]">Data Pagamento:</span>
          <span className="ml-2 font-medium text-[color:var(--text-primary)]">{formatarData(conta.data_pagamento)}</span>
        </div>
        <div>
          <span className="text-[color:var(--text-muted)]">Data Vencimento:</span>
          <span className="ml-2 font-medium text-[color:var(--text-primary)]">{formatarData(conta.data_vencimento)}</span>
        </div>
        <div>
          <span className="text-[color:var(--text-muted)]">Data Emissão:</span>
          <span className="ml-2 font-medium text-[color:var(--text-primary)]">{formatarData(conta.data_emissao)}</span>
        </div>
        
        <hr className="border-[color:var(--border)]" />
        
        <div>
          <span className="text-[color:var(--text-muted)]">Conta:</span>
          <span className="ml-2 font-medium text-xs text-[color:var(--text-primary)]">{conta.conta || '-'}</span>
        </div>
        <div>
          <span className="text-[color:var(--text-muted)]">Planejamento:</span>
          <span className="ml-2 font-medium text-xs text-[color:var(--text-primary)]">{conta.planejamento || '-'}</span>
        </div>
        <div>
          <span className="text-[color:var(--text-muted)]">Conta Analítica:</span>
          <span className="ml-2 font-medium text-xs text-[color:var(--text-primary)]">{conta.conta_analitica || '-'}</span>
        </div>
        
        {conta.tipo_pagamento && (
          <div>
            <span className="text-[color:var(--text-muted)]">Tipo Pagamento:</span>
            <span className="ml-2 font-medium text-[color:var(--text-primary)]">{conta.tipo_pagamento}</span>
          </div>
        )}
        
        {conta.documento && (
          <div>
            <span className="text-[color:var(--text-muted)]">Documento:</span>
            <span className="ml-2 font-medium text-[color:var(--text-primary)]">{conta.documento}</span>
          </div>
        )}
        
        {conta.chave_pix && (
          <div>
            <span className="text-[color:var(--text-muted)]">Chave PIX:</span>
            <span className="ml-2 font-medium text-xs text-[color:var(--text-primary)]">{conta.chave_pix}</span>
          </div>
        )}
        
        {conta.observacao && conta.observacao.trim() && (
          <div>
            <span className="text-[color:var(--text-muted)]">Observação:</span>
            <p className="mt-1 text-xs text-[color:var(--text-primary)] bg-[color:var(--surface)] p-2 rounded border border-[color:var(--border)]">
              {conta.observacao.trim()}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black/40 transition-opacity" 
          onClick={onClose}
        />

        {/* Modal */}
        <div className={`relative inline-block w-full ${isDuplicidade ? 'max-w-5xl' : 'max-w-3xl'} p-6 my-8 text-left align-middle bg-[color:var(--surface)] rounded-lg shadow-xl transform transition-all`}>
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b border-[color:var(--border)]">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
                {isDuplicidade ? 'Detalhes das Duplicidades' : 'Detalhes da Conciliação'}
              </h3>
              {isDuplicidade && (
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] border border-[color:var(--border)]">
                  {ixcContas.length} contas duplicadas
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text-muted)] rounded-full hover:bg-[color:var(--surface-muted)]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {isDuplicidade ? (
            /* Layout para Duplicidades */
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-400" />
                <p className="text-sm text-yellow-400">
                  Foram encontradas {ixcContas.length} contas no IXC com o mesmo valor, data e nome.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
                {ixcContas.map((conta, idx) => {
                  const isPaga = conta?.debito_data || conta?.valor_total_pago !== undefined;
                  return (
                    <div key={idx} className="p-4 rounded-lg border border-yellow-500/30 bg-[color:var(--surface)]">
                      <div className="flex items-center gap-2 mb-4">
                        <Building2 className="h-5 w-5 text-yellow-400" />
                        <h4 className="font-semibold text-[color:var(--text-primary)]">
                          IXC - Duplicado {idx + 1} {isPaga ? '(Paga)' : '(Recebida)'}
                        </h4>
                      </div>
                      {renderContaIxc(conta, idx)}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Layout Normal (IXC + OFX) */
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Coluna IXC */}
              <div className={`p-4 rounded-lg ${ixc ? 'border border-[color:var(--border)] bg-[color:var(--surface)] border-l-4 border-l-[color:var(--accent)]' : 'border border-[color:var(--border)] bg-[color:var(--surface-muted)]'}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className={`h-5 w-5 ${ixc ? 'text-[color:var(--accent)]' : 'text-[color:var(--text-muted)]'}`} />
                  <h4 className="font-semibold text-[color:var(--text-primary)]">
                    IXC {isContaPaga ? '(Conta Paga)' : '(Conta Recebida)'}
                  </h4>
                </div>

                {ixc ? renderContaIxc(ixc) : (
                  <p className="text-[color:var(--text-muted)] text-sm">Não encontrado no IXC</p>
                )}
              </div>

              {/* Coluna OFX */}
              <div className={`p-4 rounded-lg ${ofx ? 'border border-[color:var(--border)] bg-[color:var(--surface)] border-l-4 border-l-emerald-500/50' : 'border border-[color:var(--border)] bg-[color:var(--surface-muted)]'}`}>
                <div className="flex items-center gap-2 mb-4">
                  <FileText className={`h-5 w-5 ${ofx ? 'text-emerald-500' : 'text-[color:var(--text-muted)]'}`} />
                  <h4 className="font-semibold text-[color:var(--text-primary)]">
                    OFX (Extrato Bancário)
                  </h4>
                </div>

                {ofx ? (
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-[color:var(--text-muted)]">FITID:</span>
                      <span className="ml-2 font-medium text-xs text-[color:var(--text-primary)]">{ofx.fitid || '-'}</span>
                    </div>
                    <div>
                      <span className="text-[color:var(--text-muted)]">Descrição:</span>
                      <span className="ml-2 font-medium text-[color:var(--text-primary)]">{ofx.descricao || ofx.nome || '-'}</span>
                    </div>
                    <div>
                      <span className="text-[color:var(--text-muted)]">Valor:</span>
                      <span className={`ml-2 font-medium ${ofx.valor < 0 ? 'text-red-600' : 'text-emerald-500'}`}>
                        {formatarMoeda(ofx.valor)}
                      </span>
                    </div>
                    <div>
                      <span className="text-[color:var(--text-muted)]">Data:</span>
                      <span className="ml-2 font-medium text-[color:var(--text-primary)]">{formatarData(ofx.data)}</span>
                    </div>
                    <div>
                      <span className="text-[color:var(--text-muted)]">Tipo:</span>
                      <span className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${
                        ofx.tipo === 'DEBIT' 
                          ? 'bg-red-500/10 text-red-600 border border-red-500/20' 
                          : 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'
                      }`}>
                        {ofx.tipo === 'DEBIT' ? 'Débito' : 'Crédito'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-[color:var(--text-muted)] text-sm">Não encontrado no OFX</p>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-[color:var(--border)] flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] rounded-lg hover:bg-[color:var(--surface-muted)]"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConciliacaoDetalhesModal;








